import {
  reactive,
  ref,
  watch,
  unref,
  nextTick,
  onBeforeUnmount,
  type Ref,
  type ComponentPublicInstance,
} from 'vue';

type AxisLock = 'x' | 'y' | 'both';

type ElementLike = HTMLElement | ComponentPublicInstance | null | undefined;

type MaybeRef<T> = T | Ref<T>;

interface UseDragOptions {
  boundaryPadding?: number;
  lockAxis?: AxisLock;
  initialX?: number;
  initialY?: number;
  zIndex?: number;
}

interface DragState {
  x: number;
  y: number;
}

interface UseDragReturn {
  position: DragState;
  dragging: Ref<boolean>;
  updatePosition: (x: number, y: number) => void;
  teardown: () => void;
}

const DRAG_THRESHOLD = 5;

const DEFAULT_OPTIONS: Required<
  Pick<UseDragOptions, 'boundaryPadding' | 'lockAxis' | 'zIndex'>
> = {
  boundaryPadding: 0,
  lockAxis: 'both',
  zIndex: 9999,
};

const getPoint = (event: MouseEvent | TouchEvent) => {
  if ('touches' in event) {
    const touch = event.touches[0] || event.changedTouches[0];
    return {
      x: touch?.clientX ?? 0,
      y: touch?.clientY ?? 0,
    };
  }
  return { x: event.clientX, y: event.clientY };
};

const isComponentInstance = (
  value: ElementLike,
): value is ComponentPublicInstance => {
  return Boolean(value && typeof value === 'object' && '$el' in value);
};

const resolveElement = (target: MaybeRef<ElementLike>): HTMLElement | null => {
  const raw = unref(target);
  if (!raw) {
    return null;
  }
  if (raw instanceof HTMLElement) {
    return raw;
  }
  if (isComponentInstance(raw) && raw.$el instanceof HTMLElement) {
    return raw.$el;
  }
  return null;
};

const clamp = (value: number, min: number, max: number) => {
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

export default function useDrag(
  target: MaybeRef<ElementLike>,
  options: UseDragOptions = {},
): UseDragReturn {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const position = reactive<DragState>({
    x: options.initialX ?? 0,
    y: options.initialY ?? 0,
  });
  const dragging = ref(false);
  let pointerDown = false;

  let element: HTMLElement | null = null;
  let cleanupWatch: (() => void) | null = null;

  const pointerStart = { x: 0, y: 0 };
  const startPosition = { x: position.x, y: position.y };

  const updateTransform = () => {
    if (!element) return;
    element.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
  };

  const clampPosition = (x: number, y: number) => {
    if (!element || typeof window === 'undefined') {
      return { x, y };
    }

    const rect = element.getBoundingClientRect();
    const minX = config.boundaryPadding;
    const minY = config.boundaryPadding;
    const maxX = window.innerWidth - rect.width - config.boundaryPadding;
    const maxY = window.innerHeight - rect.height - config.boundaryPadding;

    return {
      x: clamp(x, minX, maxX),
      y: clamp(y, minY, maxY),
    };
  };

  const applyBaseStyle = () => {
    if (!element) return;
    const style = element.style;
    if (!style.position || style.position === 'static') {
      style.position = 'fixed';
      style.left = '0px';
      style.top = '0px';
    }
    style.zIndex = String(config.zIndex);
    style.touchAction = 'none';
    style.userSelect = 'none';
    style.cursor = 'grab';
    updateTransform();
  };

  const handleMove = (event: MouseEvent | TouchEvent) => {
    if (!pointerDown || !element) {
      return;
    }

    const { x, y } = getPoint(event);
    const deltaX = x - pointerStart.x;
    const deltaY = y - pointerStart.y;

    if (!dragging.value) {
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance < DRAG_THRESHOLD) {
        return;
      }
      dragging.value = true;
      if (element) {
        element.style.cursor = 'grabbing';
      }
    }

    if ('touches' in event) {
      event.preventDefault();
    }

    const nextX = startPosition.x + (config.lockAxis === 'y' ? 0 : deltaX);
    const nextY = startPosition.y + (config.lockAxis === 'x' ? 0 : deltaY);

    const clampedPosition = clampPosition(nextX, nextY);
    position.x = clampedPosition.x;
    position.y = clampedPosition.y;
    updateTransform();
  };

  const endDrag = () => {
    if (!pointerDown) {
      return;
    }
    const wasDragging = dragging.value;
    pointerDown = false;
    dragging.value = false;
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('mouseup', endDrag);
    window.removeEventListener('touchend', endDrag);
    window.removeEventListener('touchcancel', endDrag);
    if (element) {
      element.style.cursor = 'grab';
    }
    if (!wasDragging) {
      return;
    }
  };

  const startDrag = (event: MouseEvent | TouchEvent) => {
    if ('touches' in event && event.touches.length > 1) {
      return;
    }
    if (!element) {
      return;
    }
    pointerDown = true;
    dragging.value = false;

    const { x, y } = getPoint(event);
    pointerStart.x = x;
    pointerStart.y = y;
    startPosition.x = position.x;
    startPosition.y = position.y;

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
  };

  const detachTarget = () => {
    if (!element) {
      return;
    }
    element.removeEventListener('mousedown', startDrag);
    element.removeEventListener('touchstart', startDrag as EventListener);
    endDrag();
  };

  const attachTarget = () => {
    const resolved = resolveElement(target);
    if (!resolved || resolved === element) {
      return;
    }
    detachTarget();
    element = resolved;
    applyBaseStyle();
    element.addEventListener('mousedown', startDrag);
    element.addEventListener('touchstart', startDrag as EventListener, {
      passive: false,
    });
  };

  const updatePosition = (x: number, y: number) => {
    const clampedPosition = clampPosition(x, y);
    position.x = clampedPosition.x;
    position.y = clampedPosition.y;
    updateTransform();
  };

  const teardown = () => {
    detachTarget();
    if (cleanupWatch) {
      cleanupWatch();
      cleanupWatch = null;
    }
  };

  cleanupWatch = watch(
    () => resolveElement(target),
    () => {
      nextTick(() => {
        attachTarget();
      });
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    teardown();
  });

  return {
    position,
    dragging,
    updatePosition,
    teardown,
  };
}
